import React, { useState, useRef, useEffect } from 'react';
import { IoInformationCircle } from 'react-icons/io5';
import './InfoButton.css';

interface InfoButtonProps {
  elementId: string;
  className?: string;
}

// Check if we're in dev mode
const isDevMode = (): boolean => {
  return (window as any).__DEV_MODE__ === true;
};

const InfoButton: React.FC<InfoButtonProps> = ({ elementId, className = '' }) => {
  // All hooks must be called unconditionally - before any early returns
  const [isHovered, setIsHovered] = useState(false);
  const [info, setInfo] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isDevMode()) return;
    
    setIsLoading(true);
    // Import element info data
    import('../../data/elementInfo').then((module) => {
      const elementInfo = module.default || module;
      if (elementInfo && elementInfo[elementId]) {
        setInfo(elementInfo[elementId]);
      } else {
        console.warn('Element info not found for:', elementId);
      }
      setIsLoading(false);
    }).catch((err) => {
      console.error('Could not load element info for', elementId, ':', err);
      setIsLoading(false);
    });
  }, [elementId]);

  useEffect(() => {
    if (!isHovered || !tooltipRef.current || !buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current;
    const tooltipHeight = tooltip.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate position
    let top = buttonRect.top - tooltipHeight - 8; // 8px gap
    let left = buttonRect.left + (buttonRect.width / 2);
    
    // If tooltip would go above viewport, show below instead
    if (top < 1) {
      top = buttonRect.bottom + 8;
      tooltip.classList.add('tooltip-below');
    } else {
      tooltip.classList.remove('tooltip-below');
    }
    
    // Adjust horizontal position to stay within viewport
    if (left + tooltipWidth / 2 > viewportWidth - 1) {
      left = viewportWidth - tooltipWidth / 2 - 1;
    } else if (left - tooltipWidth / 2 < 1) {
      left = tooltipWidth / 2 + 1;
    }
    
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }, [isHovered]);

  // Only render in dev mode - but hooks are already called above
  if (!isDevMode()) {
    return null;
  }

  // Always show button - if info not loaded, show a placeholder
  if (isLoading) {
    return (
      <div ref={buttonRef} className={`info-button-wrapper ${className}`}>
        <IoInformationCircle className="info-button-icon" style={{ opacity: 0.3 }} />
      </div>
    );
  }

  if (!info) {
    // Show button even if no info found (for debugging)
    return (
      <div ref={buttonRef} className={`info-button-wrapper ${className}`}>
        <IoInformationCircle className="info-button-icon" style={{ color: '#ef4444' }} title={`No info found for: ${elementId}`} />
      </div>
    );
  }

  return (
    <div 
      ref={buttonRef}
      className={`info-button-wrapper ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IoInformationCircle className="info-button-icon" />
      {isHovered && (
        <div ref={tooltipRef} className="info-tooltip">
          <div className="info-tooltip-header">
            <span className="info-label">{info.label}</span>
            {info.aiCategory && (
              <span className="info-ai-badge">{info.aiCategory}</span>
            )}
          </div>
          <div className="info-tooltip-content">
            <div className="info-section">
              <strong>Function:</strong>
              <p>{info.function}</p>
            </div>
            {info.database && (
              <div className="info-section">
                <strong>Database:</strong>
                <p>{info.database}</p>
              </div>
            )}
            {info.apiVersioning && (
              <div className="info-section">
                <strong>API Versioning:</strong>
                <p>{info.apiVersioning}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoButton;

